import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/authService';

// ── Role type — SECURITY_GUARD added ─────────────────────────────────────────
export type UserRole = 'SUPER_ADMIN' | 'SUB_ADMIN' | 'END_USER' | 'SECURITY_GUARD';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  warehouseIds: string[];
  companyId: string;
  must_change_password?: boolean;
  /** Permission keys or object */
  permissions?: string[] | Record<string, any>;
  additionalControls?: Record<string, any>;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (partial: Partial<AuthUser>) => void;
  hydrateFromApi: () => Promise<void>;
  clearError: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const result = (await authService.login(email, password)) as any;

          const token =
            result.accessToken ||
            result.token ||
            result.data?.accessToken;

          const rawUser =
            result.user ||
            result.data?.user;

          if (!token || !rawUser) {
            throw new Error("Invalid login response from server");
          }

          const user: AuthUser = {
            id: rawUser.id || '',
            name: rawUser.name || rawUser.email || 'User',
            email: rawUser.email,
            role: rawUser.role,
            warehouseIds: rawUser.warehouseIds || [],
            companyId: rawUser.companyId || rawUser.company_id || 'default',
            must_change_password: Boolean(rawUser.must_change_password),
            permissions: rawUser.permissions,
            additionalControls: rawUser.additionalControls,
          };

          set({
            user,
            accessToken: token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          if (typeof window !== 'undefined') {
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
          }

          return true;
        } catch (err: any) {
          let msg = 'Login failed. Please check your credentials.';
          if (err.response?.data?.message) {
            msg = err.response.data.message;
          } else if (err.response?.data?.error) {
            msg = err.response.data.error;
          } else if (err.message && !err.response) {
            msg = 'Unable to connect to server. Please ensure the backend is running.';
          }
          set({ isLoading: false, error: msg, isAuthenticated: false });
          return false;
        }
      },

      logout: async () => {
        if (get().accessToken) {
          try {
            await authService.logout();
          } catch {
            // best-effort
          }
        }
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_company');
        }
        set({ user: null, accessToken: null, isAuthenticated: false, error: null });
      },

      updateUser: (partial: Partial<AuthUser>) => {
        set((state) => {
          if (!state.user) return state;
          const updated = { ...state.user, ...partial };
          if (typeof window !== 'undefined') {
            localStorage.setItem('user', JSON.stringify(updated));
          }
          return { user: updated };
        });
      },

      hydrateFromApi: async () => {
        if (!get().accessToken) return;
        try {
          const me = (await authService.me()) as any;
          set((state) => {
            if (!state.user) return state;
            const updated: AuthUser = {
              ...state.user,
              ...me,
              must_change_password: Boolean(me.must_change_password),
              companyId: me.companyId ?? me.company_id ?? state.user.companyId ?? 'default',
            };
            if (typeof window !== 'undefined') {
              localStorage.setItem('user', JSON.stringify(updated));
            }
            return { user: updated };
          });
        } catch {
          set({ user: null, accessToken: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'invenpro-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);